/**
 * Remembers where the visitor was in the event list so Back from a venue or event
 * page returns to the same spot. Restoring happens only on back/forward navigation;
 * following a fresh link to the list still starts at the top.
 *
 * The position is stored as the event card at the top of the screen plus its offset,
 * not only as a pixel value, because parts of the page above the list (guide picks,
 * notes, lazily loaded sections) can finish loading after the restore and push the
 * list down.
 */

const STORAGE_KEY = "oal-list-scroll";
const MAX_AGE_MS = 30 * 60 * 1000;

export interface ListScrollPosition {
  y: number;
  /** data-event-id of the card at the top of the screen. */
  anchorId: string | null;
  /** That card's distance from the top of the viewport, in pixels. */
  anchorOffset: number;
}

let lastPopStateAt = 0;
if (typeof window !== "undefined") {
  window.addEventListener("popstate", () => {
    lastPopStateAt = Date.now();
  });
}

/** True right after a Back/Forward navigation (popstate). */
export function wasBackForwardNavigation(withinMs = 2000, now = Date.now()): boolean {
  return now - lastPopStateAt < withinMs;
}

export function saveListScroll(url: string, position: ListScrollPosition): void {
  try {
    sessionStorage.setItem(STORAGE_KEY, JSON.stringify({ url, ...position, y: Math.max(0, Math.round(position.y)), at: Date.now() }));
  } catch {
    // Storage can be unavailable (private mode); losing the position is harmless.
  }
}

/** The saved position for this exact list URL, if recent. */
export function readListScroll(url: string, now = Date.now()): ListScrollPosition | null {
  try {
    const raw = sessionStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    const saved = JSON.parse(raw) as Partial<ListScrollPosition> & { url?: string; at?: number };
    if (saved.url !== url || typeof saved.y !== "number" || typeof saved.at !== "number") return null;
    if (now - saved.at > MAX_AGE_MS) return null;
    return {
      y: saved.y,
      anchorId: typeof saved.anchorId === "string" ? saved.anchorId : null,
      anchorOffset: typeof saved.anchorOffset === "number" ? saved.anchorOffset : 0,
    };
  } catch {
    return null;
  }
}

export function currentListUrl(): string {
  return `${window.location.pathname}${window.location.search}`;
}

/** The current scroll position, anchored to the first event card below `topInset` pixels. */
export function measureListScroll(topInset = 80): ListScrollPosition {
  const cards = document.querySelectorAll<HTMLElement>("[data-event-id]");
  for (const card of cards) {
    const rect = card.getBoundingClientRect();
    if (rect.bottom > topInset) {
      return { y: window.scrollY, anchorId: card.dataset.eventId || null, anchorOffset: rect.top };
    }
  }
  return { y: window.scrollY, anchorId: null, anchorOffset: 0 };
}

/** Scroll so the saved card is back at its saved offset (or to the saved pixel value). */
export function applyListScroll(position: ListScrollPosition): void {
  const anchor = position.anchorId
    ? document.querySelector<HTMLElement>(`[data-event-id="${CSS.escape(position.anchorId)}"]`)
    : null;
  const top = anchor
    ? anchor.getBoundingClientRect().top + window.scrollY - position.anchorOffset
    : position.y;
  const maxScroll = document.documentElement.scrollHeight - window.innerHeight;
  window.scrollTo({ top: Math.min(Math.max(0, top), Math.max(0, maxScroll)) });
}
